import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, Users, Lock, Check } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { recipeSrcSet } from "../lib/media";
import { Loader } from "../components/shared";
import BackToHome from "../components/BackToHome";
import { demoCopy } from "../i18n/demoCopy";
import { useDemoBuy } from "./useDemoBuy";
import { demoTrack } from "./demoMode";

const TUT_KEY = "mc_demo_recipe_tut";
const LINES = [92, 70, 84, 60, 78, 88, 52, 74];

// Demo-only recipe view: receives ONLY the server-side preview (no full ingredients/steps ever reach the browser).
export default function DemoRecipeDetail() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const C = demoCopy(lang).recipe;
  const { buy, current } = useDemoBuy();
  const [r, setR] = useState(null);
  const [tut, setTut] = useState(sessionStorage.getItem(TUT_KEY) !== "1");

  useEffect(() => { api.get(`/content/recipes/${id}?lang=${lang}`).then(({ data }) => { setR(data); demoTrack("recipe_preview", { id }); }).catch(() => navigate("/demo/app/recipes")); }, [id, lang, navigate]);
  if (!r) return <Loader />;
  const closeTut = () => { sessionStorage.setItem(TUT_KEY, "1"); setTut(false); };

  return (
    <div data-testid="demo-recipe-preview">
      <BackToHome crumbs={[{ label: t("nav.recipes"), to: "/demo/app/recipes" }]} current={r.name} />
      <button onClick={() => navigate(-1)} className="mb-6 inline-flex items-center gap-2 text-sm text-xmas-gold hover:underline"><ArrowLeft className="h-4 w-4" /> {t("cta.back")}</button>

      <div className="overflow-hidden rounded-3xl border border-xmas-gold/15">
        <div className="relative h-64 sm:h-80">
          <img src={r.image} srcSet={recipeSrcSet(r.image)} sizes="(max-width: 640px) 100vw, 1024px" alt={r.name} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-xmas-navy via-xmas-navy/30 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-6">
            <span className="rounded-full border border-xmas-gold/40 bg-xmas-navy/70 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-xmas-gold" data-testid="demo-recipe-badge">{C.preview}</span>
            <h1 className="mt-3 font-display text-3xl font-bold text-xmas-snow sm:text-4xl" data-testid="demo-recipe-name">{r.name}</h1>
            <p className="mt-2 max-w-2xl text-sm text-xmas-champagne sm:text-base">{r.description}</p>
            <div className="mt-3 flex flex-wrap gap-4 text-xs text-xmas-champagne">{r.time_minutes && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-xmas-gold" /> {r.time_minutes} min</span>}{r.servings && <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5 text-xmas-gold" /> {r.servings}</span>}</div>
          </div>
        </div>

        <div className="grid gap-8 bg-xmas-navycard/60 p-6 sm:p-8 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <h2 className="mb-4 font-display text-xl text-xmas-gold">{C.sample}</h2>
            <ul className="space-y-2.5" data-testid="demo-recipe-sample">
              {(r.ingredients_sample || []).map((ing) => <li key={ing} className="flex items-center gap-2.5 text-sm text-xmas-snow"><Check className="h-4 w-4 shrink-0 text-xmas-gold" /> {ing}</li>)}
            </ul>
            <p className="mt-4 text-xs italic text-xmas-slate" data-testid="demo-recipe-more">{C.more.replace("{n}", Math.max(0, (r.ingredients_count || 0) - (r.ingredients_sample?.length || 0))).replace("{s}", r.steps_count || 0)}</p>
          </div>

          <div className="relative select-none overflow-hidden rounded-2xl border border-xmas-gold/20 bg-xmas-navy/60 p-6" data-testid="demo-recipe-locked" aria-hidden="true">
            <div className="space-y-3 blur-[3px] [mask-image:linear-gradient(to_bottom,rgba(0,0,0,.9),rgba(0,0,0,.15))]">
              <div className="h-5 w-40 rounded bg-xmas-gold/40" />
              {LINES.map((w, i) => <div key={i} className="flex items-center gap-3"><div className="h-6 w-6 shrink-0 rounded-full bg-xmas-gold/30" /><div className="h-3 rounded bg-white/20" style={{ width: `${w}%` }} /></div>)}
            </div>
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-xmas-navy/30 via-xmas-navy/70 to-xmas-navy/90 p-5">
              <div className="w-full max-w-sm rounded-2xl border border-xmas-gold/40 bg-xmas-navycard/95 p-6 text-center shadow-2xl backdrop-blur-xl">
                <Lock className="mx-auto h-7 w-7 text-xmas-gold" />
                <p className="mt-3 font-display text-lg font-bold uppercase tracking-wide text-xmas-gold" data-testid="demo-recipe-lock-title">🔒 {C.lockT}</p>
                <p className="mt-2 text-sm text-xmas-champagne">{C.lockSub}</p>
                <button onClick={() => buy("recipe")} data-testid="demo-recipe-cta" className="btn-gold mt-5 w-full rounded-full px-6 py-3.5 text-sm font-bold uppercase tracking-wider">{C.cta}{current?.display ? ` — ${current.display}` : ""}</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {tut && (
        <div className="fixed inset-x-3 bottom-[4.5rem] z-40 mx-auto max-w-md rounded-2xl border border-xmas-gold/40 bg-xmas-navycard/95 p-5 shadow-2xl backdrop-blur-xl float-up sm:bottom-6 sm:right-6 sm:left-auto sm:mx-0" data-testid="demo-recipe-tutorial">
          <p className="font-display text-base font-bold text-xmas-snow">{C.tutT}</p>
          <p className="mt-1.5 text-sm text-xmas-champagne">{C.tutSub}</p>
          <button onClick={closeTut} data-testid="demo-recipe-tutorial-ok" className="btn-gold mt-4 rounded-full px-5 py-2 text-xs font-bold uppercase tracking-wider">{C.ok}</button>
        </div>
      )}
    </div>
  );
}
